import React, { useEffect } from "react";
import { IoOpenOutline } from "react-icons/io5";
import { useReveal } from "../../hooks/useReveal";
import {
  EVERYDAY,
  FIGURES,
  IMAGES,
  OUTCOMES,
  RESEARCH,
  TASKS,
  TRIAL,
} from "../../data/research";
import { ResearchTimeline } from "../../components/Research/ResearchTimeline";
import { OutcomeTile } from "../../components/Research/OutcomeTile";
import { Pictogram } from "../../components/Research/Pictogram";
import GradientCTA from "../../components/GradientCTA";
import styles from "./Research.module.css";

function TrialLink({
  children,
  className,
  href = TRIAL.href,
}: {
  children: React.ReactNode;
  className?: string;
  href?: string;
}) {
  return (
    <a
      href={href}
      target="_blank"
      rel="noopener noreferrer"
      className={className}
    >
      {children}
      <IoOpenOutline aria-hidden="true" />
      <span className="ui-visually-hidden"> (opens in new tab)</span>
    </a>
  );
}

function EverydayLife() {
  const ref = useReveal<HTMLElement>();
  const photo = IMAGES.everyday;
  return (
    <section ref={ref} className="ui-reveal" aria-labelledby="everyday-title">
      <h2 id="everyday-title">Back to everyday life</h2>
      <div className={styles.bento}>
        <Pictogram {...EVERYDAY} />
        <div className={styles.everydayPhoto}>
          {/* TODO: img 4, everyday task at home, 4:3 */}
          {photo.src ? (
            <figure>
              <img
                src={photo.src}
                alt={photo.alt}
                width={photo.width}
                height={photo.height}
                loading="lazy"
                decoding="async"
                style={{ objectPosition: photo.objectPosition }}
              />
              {photo.caption && <figcaption>{photo.caption}</figcaption>}
            </figure>
          ) : (
            <div className={styles.placeholder} aria-hidden="true" />
          )}
        </div>
        <div className={`ui-card ${styles.tasks}`}>
          <h3>Examples included</h3>
          <ul>
            {TASKS.map((task) => (
              <li key={task}>{task}</li>
            ))}
          </ul>
        </div>
      </div>
      <p className={styles.note}>{RESEARCH.practice}</p>
    </section>
  );
}

function Measured() {
  const ref = useReveal<HTMLElement>();
  const photo = IMAGES.measured;
  return (
    <section
      id="outcomes"
      ref={ref}
      className={`ui-reveal ${styles.measured}`}
      aria-labelledby="outcomes-title"
    >
      <h2 id="outcomes-title">What the study measured</h2>
      <p className={styles.lead}>{RESEARCH.measuredLead}</p>
      <div className={styles.outcomeLayout}>
        <figure className={styles.measurePhoto}>
          <img
            src={photo.src!}
            alt={photo.alt}
            width={photo.width}
            height={photo.height}
            loading="lazy"
            decoding="async"
            style={{ objectPosition: photo.objectPosition }}
          />
          <figcaption>{photo.caption}</figcaption>
        </figure>
        <dl className={styles.tiles}>
          {OUTCOMES.map((outcome) => (
            <OutcomeTile
              key={outcome.measure}
              outcome={outcome}
              lens="patient"
            />
          ))}
        </dl>
      </div>
      <p className={styles.source}>
        Arm function and dexterity results are posted on{" "}
        <TrialLink className="ui-link" href={TRIAL.resultsHref}>
          {TRIAL.label}
        </TrialLink>
      </p>
      <blockquote className={styles.quote}>{RESEARCH.carryOver}</blockquote>
    </section>
  );
}

export default function Research() {
  const figuresRef = useReveal<HTMLDListElement>();
  const photo = IMAGES.hero;
  useEffect(() => {
    const previousTitle = document.title;
    document.title = `${RESEARCH.title} | mRehab`;
    return () => {
      document.title = previousTitle;
    };
  }, []);
  return (
    <main id="main" className={`ui-page ${styles.page}`}>
      <div className={`ui-container ${styles.content}`}>
        <section
          className={`ui-hero-panel ui-hero-panel--split ${styles.hero}`}
          aria-labelledby="research-title"
        >
          <div className="ui-hero-copy">
            <span className="ui-section-label">Evidence</span>
            <h1 id="research-title">{RESEARCH.title}</h1>
            <p>{RESEARCH.lead}</p>
            <div className="ui-btn-row">
              <a href="#outcomes" className="ui-btn ui-btn--inverted">
                See the results
              </a>
              <TrialLink
                className={`ui-btn ui-btn--ghost ${styles.trialButton}`}
              >
                View the study
              </TrialLink>
            </div>
          </div>
          <div className="ui-hero-media">
            <img
              src={photo.src!}
              alt={photo.alt}
              width={photo.width}
              height={photo.height}
              loading="eager"
              fetchPriority="high"
              style={{ objectPosition: photo.objectPosition }}
            />
          </div>
        </section>
        <dl
          ref={figuresRef}
          className={`ui-fact-strip ui-fact-strip--4 ui-reveal ui-reveal--stagger ${styles.figures}`}
          aria-label="Research at a glance"
        >
          {FIGURES.map((fact, index) => (
            <div
              className="ui-fact"
              key={fact.label}
              style={{ "--reveal-index": index } as React.CSSProperties}
            >
              <dt className="ui-fact-label">{fact.label}</dt>
              <dd className="ui-fact-value">{fact.value}</dd>
            </div>
          ))}
        </dl>
        <ResearchTimeline lens="patient" />
        <EverydayLife />
        <Measured />
        <GradientCTA
          title="See what practice looks like"
          description="Learn how the tools and app support practice at home."
          buttonText="See how it works"
          buttonLink="/how-it-works"
        />
      </div>
    </main>
  );
}
